import { useEffect, useRef } from 'react';

const ParticleTrail = () => {
    const canvasRef = useRef();
    const particles = useRef([]);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const lerp = (start, end, amt) => start + (end - start) * amt;

        let mouseX = 0;
        let mouseY = 0;
        let smoothedX = 0;
        let smoothedY = 0;

        let lastMoveTime = 0;

        const handleMouseMove = (e) => {
            mouseX = e.clientX;
            mouseY = e.clientY;

            const now = Date.now();
            if (now - lastMoveTime < 10) return; // throttle to every 10ms
            lastMoveTime = now;
        };

        const handleResize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        const drawTriangle = (ctx, x, y, size) => {
            const height = (Math.sqrt(3) / 2) * size;
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x - size / 2, y + height);
            ctx.lineTo(x + size / 2, y + height);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
        };

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // smooth mouse position
            smoothedX = lerp(smoothedX, mouseX, 0.2);
            smoothedY = lerp(smoothedY, mouseY, 0.2);

            // particle generation
            const types = ['square', 'circle', 'triangle'];
            const type = types[Math.floor(Math.random() * types.length)];
            const colorMap = {
                square: 'rgb(255, 217, 231)',
                circle: 'rgb(220, 232, 255)',
                triangle: 'rgb(206, 227, 220)'
            };

            particles.current.push({
                x: smoothedX,
                y: smoothedY,
                size: Math.random() * 4 + 6,
                alpha: 1,
                dx: (Math.random() - 0.5) * 0.3,
                dy: (Math.random() - 0.5) * 0.3,
                type,
                color: colorMap[type]
            });

            if (particles.current.length > 300) {
                particles.current.splice(0, particles.current.length - 300);
            }

            particles.current.forEach((p, i) => {
                p.x += p.dx;
                p.y += p.dy;
                p.alpha -= 0.02;

                if (p.alpha <= 0) {
                    particles.current.splice(i, 1);
                    return;
                }

                ctx.globalAlpha = p.alpha;
                ctx.fillStyle = p.color;
                ctx.strokeStyle = 'black';
                ctx.lineWidth = 1;

                switch (p.type) {
                    case 'square':
                        ctx.fillRect(p.x, p.y, p.size, p.size);
                        ctx.strokeRect(p.x, p.y, p.size, p.size);
                        break;
                    case 'circle':
                        ctx.beginPath();
                        ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.stroke();
                        break;
                    case 'triangle':
                        drawTriangle(ctx, p.x, p.y, p.size / 1.5);
                        break;
                }
                ctx.globalAlpha = 1;
            });

            requestAnimationFrame(animate);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('resize', handleResize);
        animate();

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                pointerEvents: 'none',
                zIndex: -1,
            }}
        />
    );
};

export default ParticleTrail;
